import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { getPeriod } from 'dashboard/lib/utils/charting';
import { formatDateRangeEnd, formatDateRangeStart } from 'dashboard/lib/utils/formatters';
import {
  CaseAnalyticsAssignmentDistribution,
  CaseAnalyticsIntervals,
  CaseAnalyticsResolutionDistribution,
  CaseAnalyticsResolutionsByTag,
  CaseAnalyticsResolutionTimes,
  CaseAnalyticsStatusDistributionCurrentUser,
  CaseAnalyticsTagDistribution,
} from 'dashboard/models/case-analytics';
import { CaseTemplate, CaseTemplateStatus } from 'dashboard/models/case-template';
import { Environment } from 'dashboard/models/environment';
import {
  CaseAnalyticsFilterParams,
  setCaseAnalyticsAssignmentDistribution,
  setCaseAnalyticsIntervals,
  setCaseAnalyticsResolutionDistribution,
  setCaseAnalyticsResolutionsByTag,
  setCaseAnalyticsResolutionTimes,
  setCaseAnalyticsStatusDistributionForCurrentUser,
  setCaseAnalyticsTagDistribution,
  setCaseTemplates,
} from 'dashboard/redux/actions/case-analytics';
import { ApiEndpoint, PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface CaseIntervalsResponse {
  meta: CaseAnalyticsIntervals;
  updated_at: Iso8601DateString;
}

export function loadCaseAnalyticsIntervals({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: CaseAnalyticsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForCasesMetrics(currentEnvironment, filters);

    const response = await SessionClientDashboard.getEndpoint<CaseIntervalsResponse>(
      ApiEndpoint.MetricsCasesStatusOverTime,
      params,
    );

    dispatch(
      setCaseAnalyticsIntervals(response.meta, new Date().toISOString(), response.updated_at),
    );
  };
}

export function loadCaseAnalyticsResolutionTimes({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: CaseAnalyticsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForCasesMetrics(currentEnvironment, filters);

    const caseResolutionTimes = (
      await SessionClientDashboard.getResourceMeta<CaseAnalyticsResolutionTimes>(
        ApiEndpoint.MetricsCases,
        'resolution-times',
        params,
      )
    ).meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setCaseAnalyticsResolutionTimes(caseResolutionTimes, new Date().toISOString()));
  };
}

export function loadCaseAnalyticsAssignmentDistribution({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: CaseAnalyticsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForCasesMetrics(currentEnvironment, filters);

    const caseAssignmentDistribution = (
      await SessionClientDashboard.getResourceMeta<CaseAnalyticsAssignmentDistribution>(
        ApiEndpoint.MetricsCases,
        'assignment-distribution',
        params,
      )
    ).meta;

    dispatch(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setCaseAnalyticsAssignmentDistribution(caseAssignmentDistribution, new Date().toISOString()),
    );
  };
}

export function loadCaseAnalyticsResolutionDistribution({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: CaseAnalyticsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForCasesMetrics(currentEnvironment, filters);

    const caseResolutionDistribution = (
      await SessionClientDashboard.getResourceMeta<CaseAnalyticsResolutionDistribution>(
        ApiEndpoint.MetricsCases,
        'resolution-distribution',
        params,
      )
    ).meta;

    dispatch(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setCaseAnalyticsResolutionDistribution(caseResolutionDistribution, new Date().toISOString()),
    );
  };
}

export function loadCaseAnalyticsTagDistribution({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: CaseAnalyticsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForCasesMetrics(currentEnvironment, filters);

    const caseTagDistribution = (
      await SessionClientDashboard.getResourceMeta<CaseAnalyticsTagDistribution>(
        ApiEndpoint.MetricsCases,
        'tag-distribution',
        params,
      )
    ).meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setCaseAnalyticsTagDistribution(caseTagDistribution, new Date().toISOString()));
  };
}

export function loadCaseAnalyticsResolutionsByTag({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: CaseAnalyticsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const params = createParamsForCasesMetrics(currentEnvironment, filters);

    const caseResolutionsByTag = (
      await SessionClientDashboard.getResourceMeta<CaseAnalyticsResolutionsByTag>(
        ApiEndpoint.MetricsCases,
        'resolutions-by-tag',
        params,
      )
    ).meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setCaseAnalyticsResolutionsByTag(caseResolutionsByTag, new Date().toISOString()));
  };
}

export function loadCaseAnalyticsStatusDistributionForCurrentUser({
  currentEnvironment,
}: {
  currentEnvironment: Environment;
}) {
  return async (dispatch: Dispatch) => {
    const caseStatusDistributionForCurrentUser = (
      await SessionClientDashboard.getResourceMeta<CaseAnalyticsStatusDistributionCurrentUser>(
        ApiEndpoint.MetricsCases,
        'status-distribution-current-user',
        { filter: { 'environment-id': currentEnvironment.id } },
      )
    ).meta;

    dispatch(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setCaseAnalyticsStatusDistributionForCurrentUser(caseStatusDistributionForCurrentUser),
    );
  };
}

function createParamsForCasesMetrics(
  currentEnvironment: Environment,
  filters: CaseAnalyticsFilterParams,
) {
  const {
    startDate,
    endDate,
    dateType,
    caseTemplateIds,
    caseStateIds,
    caseTagIds,
    assigneeIds,
    teamAssigneeIds,
    resolverIds,
    teamResolverIds,
  } = filters;

  return {
    filter: {
      'environment-id': currentEnvironment.id,
      caseTemplateIds,
      caseStateIds: caseStateIds.flatMap((ids) => ids.split(',')),
      caseTagIds,
      assigneeIds,
      teamAssigneeIds,
      resolverIds,
      teamResolverIds,
      'start-date': formatDateRangeStart(startDate),
      'end-date': formatDateRangeEnd(endDate),
      'date-type': dateType,
      period: getPeriod(startDate, endDate),
    },
  };
}

export function loadCaseTemplatesForFilter() {
  return async (dispatch: Dispatch) => {
    const params = {
      filter: {
        status: CaseTemplateStatus.Active,
      },
      page: {
        // use a large page size in case an organization has many queues
        size: 100,
      },
    };

    const caseTemplatesResponse = await SessionClientDashboard.listResource<
      CaseTemplate,
      PaginationParams
    >(ApiEndpoint.CaseTemplate, params);
    const caseTemplates = caseTemplatesResponse.data;
    dispatch(setCaseTemplates(caseTemplates));
  };
}
